import {Directive, ElementRef, Input, TemplateRef, ViewChild} from '@angular/core';
import {Table} from 'primeng/table';
import {TableColumnDef} from '../../shared/tables/table-column-def';
import {FormControl} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {GeneralService} from 'app/core';

@Directive()
export abstract class BaseTable {

    @ViewChild('dt', {static: false}) dt: Table;
    @ViewChild('tableWrapper', {static: false}) tableWrapper: ElementRef;

    @Input() columnsData: Array<TableColumnDef>;
    @Input() tableId: string;
    @Input() canNavigate = false;
    @Input() navigationArr: Array<string> = [];
    @Input() customCellName = null;
    @Input() customCellTmpl: TemplateRef<any>;
    @Input() extraHeaderFieldTmpl: TemplateRef<any>;
    @Input() title: string;

    filterGlobalControl = new FormControl('');
    selectedColumnsControl = new FormControl([]);

    idFieldName: string;
    showTable = false;

    protected constructor(
        protected router: Router,
        public generalService: GeneralService,
        public activatedRoute: ActivatedRoute
    ) {

    }

    abstract clearGlobalFilter(): void;

    selectedColumnsChanged() {
        if (this.dt && this.dt.columnWidthsState) {
            this.dt.clearState();

            const oldColumnsWidth: string[] = this.dt.columnWidthsState.split(',');

            if (oldColumnsWidth.length < this.selectedColumnsControl.value.length) {

                const totalLength = oldColumnsWidth.length;
                const numberToAdd = Math.round(100 / totalLength);

                const newColumnsWidth = oldColumnsWidth.map((val: string, index) => {
                    return (parseInt(val) - numberToAdd);
                });
                newColumnsWidth.push(numberToAdd * totalLength);

                this.dt.columnWidthsState = newColumnsWidth.join(',');
                this.dt.restoreColumnWidths();
            }
        }

        this.saveTableState();
        this.clearStateFromFilters();
    }

    onColReorder(event: { dragIndex: number, dragDrop: number, columns: TableColumnDef[] }) {
        this.saveTableState();
        this.clearStateFromFilters();
    }

    onColResize(event: any) {
        this.clearStateFromFilters();
    }

    goToUrl(row) {
        if (this.canNavigate && this.navigationArr.length) {
            this.router.navigate([
                ...this.navigationArr,
                row[this.idFieldName],
            ]);
        }
    }

    resetTable() {
        this.dt.clear();
        this.dt.clearState();
        this.showTable = false;

        setTimeout(() => {
            this.selectedColumnsControl.setValue(this.columnsData);
            this.showTable = true;
            this.saveTableState();
        }, 0);
    }

    refreshTable() {
        this.dt.reset();
        this.showTable = false;

        setTimeout(() => {
            this.showTable = true;
        }, 0);
    }

    getTooltip(column: Object, row: any): string {
        try {
            switch (column['type']) {
                case 'date':
                    return row ? this.generalService.parseDate(row) : row;
                    break;
                case 'vat':
                    return row
                        ? this.generalService.parseVAT(row).toFixed(0)
                        : row;
                    break;
                case 'margin':
                    return row
                        ? this.generalService.parseVAT(row).toFixed(1)
                        : row;
                default:
                    break;
            }
            switch (column['suffix']) {
                case 'currency':
                    return row ? row.toFixed(2) : row;
                    break;
                default:
                    return row;
                    break;
            }
        } catch (e) {
            console.log(e);
            console.log('error in type');
        }
    }

    protected setAllParameters() {
        const fromParentId = this.activatedRoute.parent.snapshot.paramMap.get('id');
        const urlId = this.activatedRoute.snapshot.paramMap.get('id');

        this.idFieldName = this.columnsData.shift().columnDef;
        this.tableId = 'Table_' + this.tableId + (fromParentId || '') + (urlId || '');

        this.checkAllColumnIfExist();
        this.checkWidths();

        this.selectedColumnsControl.valueChanges.subscribe((val: TableColumnDef[]) => {
            if (this.dt) {
                if (this.columnsData.length > val.length) {
                    const removedColumn: TableColumnDef = this.columnsData.filter(x => !val.includes(x))[0];
                    if (this.dt.filters[removedColumn.columnDef]) {
                        this.dt.filter('', removedColumn.columnDef, 'contains');
                    }
                }
                this.selectedColumnsChanged();
            }
        });

        this.setSelectedColumns();
    }

    protected saveTableState() {
        if (this.dt) {
            if (this.dt.filters['global']) {
                this.filterGlobalControl.setValue(this.dt.filters['global'].value);
            }
            // Zła kolejka po stronie Tabeli, setTImeout wyrzuca saveState na koniec wykonania
            setTimeout(() => {
                this.dt.saveState();
            }, 0);
        }
    }

    protected clearStateFromFilters() {
        // Metoda this.dt.getStorage() tez jest zle ustawiona w kolejce...
        // setTimeout(() => {
        //     const state = JSON.parse(localStorage.getItem(this.tableId));
        //
        //     if (state && state.filters) {
        //         delete state.filters;
        //
        //         localStorage.setItem(this.tableId, JSON.stringify(state));
        //     }
        // }, 0);
    }

    private setSelectedColumns() {
        const tableSettings = JSON.parse(localStorage.getItem(this.tableId));

        if (tableSettings && tableSettings.columnOrder) {
            const selectedColumns = this.columnsData.filter((column) => {
                return !!tableSettings.columnOrder.find(col => col === column.field);
            });

            this.selectedColumnsControl.setValue(selectedColumns);
        } else {
            this.selectedColumnsControl.setValue(this.columnsData);
        }
    }

    private checkWidths() {
        const tableSettings = JSON.parse(localStorage.getItem(this.tableId));

        if (tableSettings && this.tableWrapper) {
            // setTimeout(() => {
            const tableWidth = this.tableWrapper.nativeElement.offsetWidth;
            const oldColumnsWidth = tableSettings.columnWidths.split(',');
            const columnWidth = oldColumnsWidth.reduce((a, b) => a + parseInt(b), 0);

            if (tableWidth < columnWidth) {
                const totalLengthToCut = columnWidth - tableWidth + 40;

                const newColumnsWidth = oldColumnsWidth.map((val: string) => {
                    const percentColumn = Math.round((parseInt(val) * 100) / columnWidth);
                    const lengthToCut = Math.round((totalLengthToCut * percentColumn) / 100);
                    return (parseInt(val) - lengthToCut);
                });

                localStorage.setItem(this.tableId, JSON.stringify({
                    ...tableSettings,
                    columnWidths: newColumnsWidth.join(',')
                }));
            }
        }

        setTimeout(() => {
            this.showTable = true;
        }, 0);
    }

    private checkAllColumnIfExist() {
        const tableSettings = JSON.parse(localStorage.getItem(this.tableId));

        if (tableSettings) {

            if (tableSettings.selection) {
                delete tableSettings.selection;
                localStorage.setItem(this.tableId, JSON.stringify({
                    ...tableSettings
                }));
            }

            const columnOrder: string[] = tableSettings.columnOrder;
            const columnsToRemoveIndex: number[] = [];

            const columnsToRemove: string[] = columnOrder.filter((columnName, index) => {
                if (!(!!this.columnsData.find(col => col.field === columnName))) {
                    columnsToRemoveIndex.push(index);
                    return true;
                }

                return false;
            });

            if (tableSettings.sortField && !(!!this.columnsData.find(col => col.field === tableSettings.sortField))) {
                delete tableSettings.sortField;
                delete tableSettings.sortOrder;

                localStorage.setItem(this.tableId, JSON.stringify({
                    ...tableSettings
                }));
            }

            if (tableSettings.filters && !(!!this.columnsData.find(col => col.field === Object.keys(tableSettings.filters)[0]))) {
                delete tableSettings.filters;

                localStorage.setItem(this.tableId, JSON.stringify({
                    ...tableSettings
                }));
            }

            if (columnsToRemove.length > 0) {
                const oldColumnsWidth = tableSettings.columnWidths.split(',');

                const newColumns = columnOrder.filter((col, index) => {
                    return columnsToRemoveIndex.indexOf(index) === -1;
                });

                const newColumnsWidths = oldColumnsWidth.filter((val, index) => {
                    return columnsToRemoveIndex.indexOf(index) === -1;
                });

                localStorage.setItem(this.tableId, JSON.stringify({
                    ...tableSettings,
                    columnWidths: newColumnsWidths.join(','),
                    columnOrder: newColumns
                }));
            }
        }
    }
}
